// Generic hook for fetching dynamic field options from the backend (via @startus/lib).
// Returns an empty array on error so the filter degrades gracefully (no options shown);
// the failure is still reported to Sentry.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException, fetchFieldOptions, type FieldOption } from '@startus/lib';

interface UseFieldOptionsResult {
  options: FieldOption[];
  loading: boolean;
}

export function useFieldOptions(fieldName: string): UseFieldOptionsResult {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) ?? 'es';

  // Start with loading:true so no sync setState is needed inside the effect
  const [state, setState] = useState<UseFieldOptionsResult>({ options: [], loading: true });

  useEffect(() => {
    let cancelled = false;
    fetchFieldOptions(fieldName, lang)
      .then((data) => {
        if (!cancelled) setState({ options: data, loading: false });
      })
      .catch((err: unknown) => {
        captureException(err, { feature: 'admin-field-options', fieldName });
        if (!cancelled) setState({ options: [], loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [fieldName, lang]);

  return state;
}
